import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  Mail,
  User,
  Check,
  Play,
  RotateCcw,
  Clock,
  Target,
  Share2,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { QUIZ_QUESTIONS, ARCHETYPE_META, computeResult } from "@/data/quiz";
import { useArchetypeCopy, getComboFrom, getShadowFrom } from "@/data/results";
import SpiderChart from "@/components/SpiderChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "pyg.quiz.v3";
const STEP_INTRO = -1;

/** Fisher–Yates shuffle (non-mutating). */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Returns a fresh quiz with questions and options randomly ordered.
 *  Each question keeps its original id so scoring is unaffected by display order. */
function buildShuffledQuiz() {
  return shuffle(QUIZ_QUESTIONS).map((q) => ({
    ...q,
    options: shuffle(q.options),
  }));
}

export default function QuizDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(STEP_INTRO);
  const [answers, setAnswers] = useState({}); // { q1: {code,points}, ... }
  const [questions, setQuestions] = useState(QUIZ_QUESTIONS); // randomized per session
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [companyMatch, setCompanyMatch] = useState(null); // {id, name, code} | "invalid" | null
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [hasResume, setHasResume] = useState(false);
  const [consent, setConsent] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const { copy: archetypeCopy } = useArchetypeCopy();

  const total = questions.length + 1; // +1 for lead form
  const answeredCount = Object.keys(answers).length;
  const progress = useMemo(
    () => Math.round((Math.max(step, 0) / total) * 100),
    [step, total]
  );

  // On open: check if there's a saved unfinished session
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setHasResume(false);
        return;
      }
      const saved = JSON.parse(raw);
      if (saved && saved.answers && Object.keys(saved.answers).length > 0) {
        setHasResume(true);
      } else {
        setHasResume(false);
      }
    } catch {
      setHasResume(false);
    }
  }, [open]);

  // Persist answers + step + question/option order on every change
  useEffect(() => {
    if (step === STEP_INTRO || step === total) return; // don't persist intro/result
    try {
      // Save only ids (question + option index) — not the full text — to keep storage small
      const orderSnapshot = questions.map((q) => ({
        id: q.id,
        opts: q.options.map((o) => o.code),
      }));
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, answers, name, email, order: orderSnapshot })
      );
    } catch {
      /* ignore */
    }
  }, [step, answers, name, email, total, questions]);

  const clearStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  // Debounced company code lookup
  useEffect(() => {
    const raw = companyCode.trim();
    if (!raw) {
      setCompanyMatch(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/companies/lookup/${encodeURIComponent(raw)}`);
        setCompanyMatch(data);
      } catch {
        setCompanyMatch("invalid");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [companyCode]);

  const reset = () => {
    setStep(STEP_INTRO);
    setAnswers({});
    setName("");
    setEmail("");
    setResult(null);
    setSubmitting(false);
    setHasResume(false);
    setQuestions(QUIZ_QUESTIONS);
    setConsent(false);
    setConsentAt(null);
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 300);
  };

  const startFresh = () => {
    if (!consent) {
      toast.error("Necesitas aceptar la política de privacidad para continuar.");
      return;
    }
    clearStorage();
    setAnswers({});
    setName("");
    setEmail("");
    setHasResume(false);
    setQuestions(buildShuffledQuiz());
    setConsentAt(new Date().toISOString());
    setStep(0);
  };

  const resumeSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved) {
        setAnswers(saved.answers || {});
        setName(saved.name || "");
        setEmail(saved.email || "");
        // Restore the exact question/option order so step indexes still align
        if (Array.isArray(saved.order) && saved.order.length === QUIZ_QUESTIONS.length) {
          const byId = Object.fromEntries(QUIZ_QUESTIONS.map((q) => [q.id, q]));
          const restored = saved.order
            .map((o) => {
              const orig = byId[o.id];
              if (!orig) return null;
              const orderedOpts = o.opts
                .map((code) => orig.options.find((opt) => opt.code === code))
                .filter(Boolean);
              return { ...orig, options: orderedOpts.length === 4 ? orderedOpts : orig.options };
            })
            .filter(Boolean);
          if (restored.length === QUIZ_QUESTIONS.length) setQuestions(restored);
          else setQuestions(buildShuffledQuiz());
        } else {
          setQuestions(buildShuffledQuiz());
        }
        setStep(typeof saved.step === "number" ? saved.step : 0);
      } else {
        setQuestions(buildShuffledQuiz());
        setStep(0);
      }
    } catch {
      setQuestions(buildShuffledQuiz());
      setStep(0);
    }
    setHasResume(false);
  };

  const handleSelect = (qIndex, opt) => {
    setAnswers((a) => ({
      ...a,
      [questions[qIndex].id]: { code: opt.code, points: opt.points },
    }));
    setTimeout(() => setStep(qIndex + 1), 200);
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  const submitLead = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Necesitamos tu nombre y email para enviarte tu resultado.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Por favor ingresa un email válido.");
      return;
    }
    if (companyCode.trim() && companyMatch === "invalid") {
      toast.error("Ese código de empresa no es válido. Bórralo o ingresa uno correcto.");
      return;
    }
    // Build the ordered answer list using the canonical (non-shuffled) order
    const ordered = QUIZ_QUESTIONS.map((q) => answers[q.id]).filter(Boolean);
    if (ordered.length < QUIZ_QUESTIONS.length) {
      toast.error("Por favor responde todas las preguntas.");
      return;
    }
    const computed = computeResult(ordered);

    setSubmitting(true);
    try {
      const { data: respData } = await axios.post(`${API}/quiz/submit`, {
        name: name.trim(),
        email: email.trim(),
        answers: ordered,
        scores: computed.scores,
        archetype: computed.dominant,
        secondary: computed.secondary,
        consent: true,
        consent_at: consentAt || new Date().toISOString(),
        company_code: companyCode.trim() || null,
        tz: (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
          catch { return null; }
        })(),
      });
      setResult({
        dominant: ARCHETYPE_META[computed.dominant],
        secondary: ARCHETYPE_META[computed.secondary],
        scores: computed.scores,
        shareUrl: respData?.share_url,
        leadId: respData?.id,
        emailSent: !!respData?.email_status?.sent,
        evolutionToken: respData?.evolution_token || null,
      });
      // Auto-login: persist the access token issued by /api/quiz/submit so
      // the user can deep-link straight into /mi-evolucion (no email
      // round-trip needed). Falls back to the magic-link flow if missing.
      if (respData?.evolution_token) {
        try {
          localStorage.setItem("pyg_access_token", respData.evolution_token);
          localStorage.setItem(
            "pyg_access_token_meta",
            JSON.stringify({
              lead_id: respData.id,
              archetype: computed.dominant,
              secondary: computed.secondary,
              issued_at: new Date().toISOString(),
              source: "quiz_submit",
            })
          );
        } catch { /* localStorage disabled — magic link in email still works */ }
      }
      setStep(total);
      clearStorage();
      toast.success("¡Tu resultado está listo!");
    } catch (err) {
      console.error(err);
      toast.error("No pudimos enviar tu resultado. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- render helpers ---
  const renderQuestion = () => {
    const q = questions[step];
    const selected = answers[q.id]?.code;
    return (
      <div className="space-y-7" data-testid={`quiz-question-${step}`}>
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#29BC9B]">
            Pregunta {step + 1} / {questions.length}
          </div>
          <h3 className="text-xl md:text-2xl font-display text-white leading-tight">
            {q.text}
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-2.5">
          {q.options.map((opt) => {
            const active = selected === opt.code;
            return (
              <button
                key={opt.code + opt.text}
                onClick={() => handleSelect(step, opt)}
                data-testid={`quiz-option-${step}-${opt.code}`}
                className={`text-left px-5 py-4 rounded-sm border transition-all duration-300 group ${
                  active
                    ? "border-[#29BC9B] bg-[#29BC9B]/10"
                    : "border-white/8 bg-[#0A0A0A] hover:border-[#29BC9B]/50 hover:bg-[#0F0F0F]"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-white/90 group-hover:text-white">
                    {opt.text}
                  </span>
                  <ArrowRight
                    className={`w-4 h-4 transition-all flex-shrink-0 ${
                      active ? "text-[#29BC9B] translate-x-1" : "text-white/30"
                    }`}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLeadForm = () => (
    <div className="space-y-8" data-testid="quiz-lead-form">
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#29BC9B] flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Último paso
        </div>
        <h3 className="text-2xl md:text-3xl font-display text-white leading-tight">
          ¿A dónde enviamos tu forma de jugar?
        </h3>
        <p className="text-sm text-white/50">
          Te mostraremos tu arquetipo dominante y secundario, y cómo evolucionan bajo presión.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="quiz-name" className="text-[10px] uppercase tracking-[0.3em] text-white/60">
            <User className="inline w-3 h-3 mr-2" /> Nombre
          </Label>
          <Input
            id="quiz-name"
            data-testid="quiz-input-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre"
            className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-sm focus-visible:ring-[#29BC9B]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="quiz-email" className="text-[10px] uppercase tracking-[0.3em] text-white/60">
            <Mail className="inline w-3 h-3 mr-2" /> Email
          </Label>
          <Input
            id="quiz-email"
            data-testid="quiz-input-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-sm focus-visible:ring-[#29BC9B]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="quiz-company" className="text-[10px] uppercase tracking-[0.3em] text-white/60">
            <Building2 className="inline w-3 h-3 mr-2" /> Código de empresa
            <span className="ml-2 text-white/30 normal-case tracking-normal">(opcional)</span>
          </Label>
          <Input
            id="quiz-company"
            data-testid="quiz-input-company"
            value={companyCode}
            onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
            placeholder="Ej. ROISELLER2026"
            className={`bg-[#0A0A0A] text-white h-12 rounded-sm focus-visible:ring-[#29BC9B] uppercase tracking-wider ${
              companyMatch === "invalid"
                ? "border-red-500/60"
                : companyMatch?.name
                ? "border-[#29BC9B]/60"
                : "border-white/10"
            }`}
          />
          {companyMatch?.name && (
            <p data-testid="quiz-company-match" className="text-[11px] text-[#29BC9B] flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Empresa detectada: <span className="font-medium">{companyMatch.name}</span>
            </p>
          )}
          {companyMatch === "invalid" && companyCode.trim() && (
            <p data-testid="quiz-company-invalid" className="text-[11px] text-red-400/90">
              Código no válido. Déjalo vacío si no tienes uno.
            </p>
          )}
        </div>
      </div>

      <button
        data-testid="quiz-submit-btn"
        onClick={submitLead}
        disabled={submitting}
        className="btn-teal w-full justify-center disabled:opacity-50"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Procesando</>
        ) : (
          <>Revelar mi arquetipo <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
    </div>
  );

  // Avatar-specific "now you know how you play" subtitle — bridges
  // the result reveal into the "Quiero jugar mejor bajo presión" CTA.
  // Speaks directly to the user's discovered strength + its shadow.
  const RESULT_BRIDGE_COPY = {
    leon: "Tu ventaja es la acción decisiva. Aprende a regular tu impulsividad bajo presión para no quemar puentes valiosos en decisiones importantes.",
    lobo: "Tu ventaja es el análisis profundo y la estructura. Aprende a evitar que tu fortaleza te paralice cuando las decisiones importantes exigen velocidad.",
    halcon: "Tu ventaja es la visión estratégica. Aprende a aterrizarla en acción concreta antes de que la oportunidad se mueva sin ti.",
    delfin: "Tu ventaja es la conexión humana. Aprende a sostener tu posición cuando la presión exige decisiones impopulares.",
  };

  const renderResult = () => {
    if (!result) return null;
    const { dominant, secondary, scores } = result;
    const combo = getComboFrom(archetypeCopy, dominant.code, secondary.code);
    const shadow = getShadowFrom(archetypeCopy, dominant.code);
    const bridgeCopy = RESULT_BRIDGE_COPY[dominant.code] || "";
    return (
      <div className="space-y-10" data-testid="quiz-result">
        {/* DOMINANT */}
        <div className="text-center space-y-5">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] flex items-center justify-center gap-2">
            <Check className="w-3 h-3" /> Tu forma de jugar dominante
          </div>
          <div className="flex justify-center">
            <div
              className="w-28 h-28 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${dominant.color}22 0%, transparent 70%)`,
                border: `1px solid ${dominant.color}40`,
              }}
            >
              <img
                src={dominant.img}
                alt={dominant.name}
                className="w-16 h-16 object-contain"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </div>
          </div>
          <h2 className="font-display text-5xl md:text-6xl text-white">{dominant.name}</h2>
          <p className="text-white/60 italic text-base">{dominant.tagline}</p>
        </div>

        {/* SECONDARY badge */}
        <div className="flex items-center justify-center gap-3 text-sm">
          <span className="text-white/40">Secundario:</span>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]">
            <img
              src={secondary.img}
              alt={secondary.name}
              className="w-4 h-4 object-contain"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <span className="font-display tracking-wider text-white">{secondary.name}</span>
          </div>
        </div>

        {/* VENTA ESTRATÉGICA */}
        {combo && (
          <div
            className="p-6 md:p-7 rounded-sm border border-[#29BC9B]/30 bg-[#29BC9B]/[0.04]"
            data-testid="result-venta"
          >
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#29BC9B] mb-3">
              Tu ventaja estratégica
            </div>
            <h4 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">
              {combo.title}
            </h4>
            <p className="text-sm md:text-[15px] text-white/80 leading-relaxed">
              {combo.body}
            </p>
          </div>
        )}

        {/* MODO SOMBRA */}
        {shadow && (
          <div
            className="p-6 md:p-7 rounded-sm border border-white/10 bg-[#0A0A0A]"
            data-testid="result-shadow"
          >
            <div className="text-[10px] uppercase tracking-[0.35em] text-white/50 mb-3 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-red-400/70" /> Tu modo sombra
            </div>
            <h4 className="font-display text-xl md:text-2xl text-white mb-5 leading-tight">
              Cuando tu {dominant.name} entra en modo sombra
            </h4>
            <ul className="space-y-2.5 mb-5">
              {shadow.bullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm text-white/75 leading-relaxed">
                  <span className="text-white/30 mt-1.5 flex-shrink-0">—</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="pt-4 border-t border-white/5">
              <p className="text-sm text-white italic leading-relaxed">
                <span className="text-[#29BC9B] mr-1">→</span>
                {shadow.danger}
              </p>
            </div>
          </div>
        )}

        {/* SCORES — Spider/Radar */}
        <div className="space-y-3 pt-4">
          <div className="text-center space-y-1.5 mb-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#29BC9B]">
              Tu reparto de puntos
            </div>
            <p className="text-xs text-white/40">
              Distribución de tu comportamiento en acción, análisis, visión y conexión
            </p>
          </div>
          <SpiderChart scores={scores} />
        </div>

        <button
          data-testid="quiz-close-btn"
          onClick={() => handleClose(false)}
          className="btn-ghost w-full justify-center"
        >
          Cerrar
        </button>

        {/* CONVERSION BRIDGE — repositioning the result modal from
            "view a page" to "want to play better".
            1. Avatar-specific bridge copy (curiosity hook).
            2. Primary teal CTA → /mi-evolucion?token=... (auto-login).
            3. Share kept as secondary (clipboard / native share). */}
        <div className="pt-2 space-y-5">
          <p
            data-testid="quiz-result-bridge"
            className="text-center text-[14px] md:text-[15px] leading-relaxed text-white/75 max-w-[520px] mx-auto"
          >
            <span className="text-white">
              Ahora que sabes que juegas como <strong>{dominant.name}</strong>
              {secondary?.name ? <> con <strong>{secondary.name}</strong></> : null}.
            </span>{" "}
            {bridgeCopy}
          </p>

          <button
            data-testid="quiz-evolve-cta-btn"
            onClick={() => {
              const token = result.evolutionToken;
              const target = token
                ? `/mi-evolucion?token=${encodeURIComponent(token)}`
                : "/acceder";
              window.location.assign(target);
            }}
            className="w-full justify-center inline-flex items-center gap-2 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-6 py-4 rounded-sm transition-all duration-300 shadow-[0_8px_30px_-10px_rgba(41,188,155,0.55)]"
          >
            Quiero jugar mejor bajo presión
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            data-testid="quiz-share-btn"
            onClick={async () => {
              if (!result.shareUrl) return;
              try {
                if (navigator.share) {
                  await navigator.share({
                    title: `Soy ${result.dominant.name} en PLAY YOUR GAME™`,
                    text: "Descubrí mi forma de jugar bajo presión.",
                    url: result.shareUrl,
                  });
                } else {
                  await navigator.clipboard.writeText(result.shareUrl);
                  toast.success("Enlace copiado");
                }
              } catch {
                /* user cancelled */
              }
            }}
            className="btn-ghost w-full justify-center"
          >
            <Share2 className="w-3.5 h-3.5" /> Compartir resultado
          </button>
        </div>

        {result.emailSent && (
          <p className="text-center text-[10px] uppercase tracking-[0.25em] text-white/40">
            Te enviamos el resultado completo por email.
          </p>
        )}
      </div>
    );
  };

  const renderIntro = () => {
    const totalQ = QUIZ_QUESTIONS.length;
    const totalPts = QUIZ_QUESTIONS.reduce(
      (acc, q) => acc + Math.max(...q.options.map((o) => o.points)),
      0
    );
    return (
      <div className="space-y-8" data-testid="quiz-intro">
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Test PLAY YOUR GAME
          </div>
          <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
            Estás a {totalQ} preguntas de descubrir tu{" "}
            <span className="text-[#29BC9B]">ventaja estratégica</span>.
          </h3>
          <p className="text-sm text-white/60 leading-relaxed">
            Responde con honestidad. No hay respuestas correctas: cada opción
            suma puntos a uno de los 4 arquetipos. Al final sabrás tu forma
            dominante de jugar y la sombra que aparece bajo presión.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-sm border border-white/8 bg-[#0A0A0A]">
            <Clock className="w-4 h-4 text-[#29BC9B] mb-2" />
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-1">
              Duración
            </div>
            <div className="text-sm text-white">~4 min</div>
          </div>
          <div className="p-4 rounded-sm border border-white/8 bg-[#0A0A0A]">
            <Target className="w-4 h-4 text-[#29BC9B] mb-2" />
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-1">
              Preguntas
            </div>
            <div className="text-sm text-white">{totalQ}</div>
          </div>
          <div className="p-4 rounded-sm border border-white/8 bg-[#0A0A0A]">
            <Sparkles className="w-4 h-4 text-[#29BC9B] mb-2" />
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-1">
              Puntos
            </div>
            <div className="text-sm text-white">{totalPts} máx</div>
          </div>
        </div>

        {hasResume ? (
          <div className="space-y-3">
            <div className="p-4 rounded-sm border border-[#29BC9B]/30 bg-[#29BC9B]/[0.05]">
              <p className="text-sm text-white">
                Tienes un test sin terminar.{" "}
                <span className="text-white/60">
                  Llevas {answeredCount > 0 ? answeredCount : "algunas"}{" "}
                  respuestas guardadas.
                </span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={resumeSaved}
                data-testid="quiz-resume-btn"
                className="btn-teal w-full justify-center"
              >
                Continuar <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={startFresh}
                data-testid="quiz-fresh-btn"
                className="btn-ghost w-full justify-center"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Empezar de nuevo
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <label
              htmlFor="consent-checkbox"
              className="flex items-start gap-3 p-4 rounded-sm border border-white/10 bg-[#0A0A0A] hover:border-[#29BC9B]/40 transition-colors cursor-pointer"
            >
              <input
                id="consent-checkbox"
                type="checkbox"
                data-testid="quiz-consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#29BC9B] flex-shrink-0 cursor-pointer"
              />
              <span className="text-xs text-white/70 leading-relaxed">
                He leído y acepto la{" "}
                <a
                  href="/privacidad"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#29BC9B] underline hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  política de privacidad y protección de datos
                </a>
                . Autorizo a PLAY YOUR GAME™ a tratar mi nombre, email y respuestas para enviarme mi resultado por correo.
              </span>
            </label>

            <button
              onClick={startFresh}
              data-testid="quiz-start-btn"
              disabled={!consent}
              className="btn-teal w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" /> Empezar el test
            </button>
          </div>
        )}
      </div>
    );
  };

  const showBack = step > 0 && step <= questions.length && step !== total;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="quiz-dialog"
        className="bg-[#070707] border border-white/8 text-white max-w-2xl p-0 rounded-md overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Test PLAY YOUR GAME</DialogTitle>
          <DialogDescription>Descubre tu forma de jugar bajo presión.</DialogDescription>
        </DialogHeader>

        {step >= 0 && step < total && (
          <div className="px-2 pt-2 sticky top-0 bg-[#070707] z-10">
            <Progress
              value={progress}
              className="h-[2px] bg-white/5 [&>div]:bg-[#29BC9B]"
            />
          </div>
        )}

        <div className="p-8 md:p-10">
          {step === STEP_INTRO && renderIntro()}
          {step >= 0 && step < questions.length && renderQuestion()}
          {step === questions.length && renderLeadForm()}
          {step === total && renderResult()}

          {showBack && (
            <button
              onClick={handleBack}
              data-testid="quiz-back-btn"
              className="mt-8 text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white inline-flex items-center gap-2 transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> Atrás
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
